/**
 * Organizations Database Module
 * 
 * CRUD operations for organizations and org members.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { handleError } from '@/utils/errorHandler';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  avatar_url: string | null;
  created_at: string;
  created_by: string;
}

export interface OrgMember {
  id: string;
  org_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  invited_by: string | null;
  joined_at: string;
  created_at: string;
  // Joined fields
  email?: string;
  display_name?: string;
  avatar_url?: string | null;
}

export interface OrgInvite {
  id: string;
  email: string;
  role: string;
  invited_by: string;
  created_at: string;
}

// --- Queries ---

export async function getMyOrganizations(): Promise<Organization[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('organizations')
      .select('*')
      .order('created_at', { ascending: true })
  );

  if (error) {
    handleError(error, { level: 'error', message: 'Error loading organizations' });
    return [];
  }

  return data || [];
}

export async function getOrganizationById(id: string): Promise<Organization | null> {
  const { data, error } = await withRetry(() =>
    supabase.from('organizations').select('*').eq('id', id).single()
  );

  if (error) {
    handleError(error, { level: 'error', message: 'Error loading organization' });
    return null;
  }

  return data;
}

export async function getOrgMembers(orgId: string): Promise<OrgMember[]> {
  const { data, error } = await withRetry(() =>
    supabase.rpc('get_org_members_with_profiles', { p_org_id: orgId })
  );

  if (error) {
    // Fallback to basic query if RPC not available
    const { data: fallbackData, error: fallbackError } = await withRetry(() =>
      supabase
        .from('org_members')
        .select('*')
        .eq('org_id', orgId)
        .order('joined_at', { ascending: true })
    );
    if (fallbackError) {
      handleError(fallbackError, { level: 'error', message: 'Error loading members' });
      return [];
    }
    return (fallbackData || []).map((m) => ({
      ...(m as OrgMember),
      display_name: (m as OrgMember).display_name || String((m as OrgMember).user_id || '').slice(0, 8) || 'User',
    }));
  }

  return data || [];
}

export async function getOrgPendingInvites(orgId: string): Promise<OrgInvite[]> {
  const { data, error } = await withRetry(() =>
    supabase.rpc('get_org_pending_invites', { p_org_id: orgId })
  );

  if (error) {
    handleError(error, { level: 'error', message: 'Error loading invites' });
    return [];
  }

  return data || [];
}

export async function inviteOrgMemberByEmail(orgId: string, email: string, role: string = 'member'): Promise<{ status: 'invited' }> {
  const { data, error } = await withRetry(() =>
    supabase.rpc('invite_org_member_by_email', { p_org_id: orgId, p_email: email, p_role: role })
  );

  if (error) {
    throw new Error(error.message || 'Error inviting member');
  }

  return data;
}

export async function cancelOrgInvite(inviteId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.from('org_invites').delete().eq('id', inviteId)
  );

  if (error) {
    throw new Error(error.message || 'Error canceling invite');
  }
}

export async function acceptPendingOrgInvites(): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.rpc('accept_pending_org_invites')
  );

  if (error) {
    handleError(error, { level: 'warning', message: 'Error accepting pending invites' });
  }
}

// --- Mutations ---

export async function createOrganization(name: string, slug: string): Promise<Organization> {
  const { data, error } = await withRetry(() =>
    supabase.rpc('create_organization', { p_name: name, p_slug: slug })
  );

  if (error) {
    if (error.message?.includes('already exists')) {
      throw new Error(`An organization with slug "${slug}" already exists`);
    }
    throw new Error(error.message || 'Error creating organization');
  }

  return data as Organization;
}

export async function updateOrganization(id: string, updates: { name?: string; slug?: string; avatar_url?: string | null }): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.from('organizations').update(updates).eq('id', id)
  );

  if (error) {
    throw new Error(error.message || 'Error updating organization');
  }
}

export async function deleteOrganization(id: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.from('organizations').delete().eq('id', id)
  );

  if (error) {
    throw new Error(error.message || 'Error deleting organization');
  }
}

export async function inviteOrgMember(orgId: string, userId: string, role: string = 'member'): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.rpc('invite_org_member', { p_org_id: orgId, p_user_id: userId, p_role: role })
  );

  if (error) {
    throw new Error(error.message || 'Error inviting member');
  }
}

export async function removeOrgMember(orgId: string, userId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.rpc('remove_org_member', { p_org_id: orgId, p_user_id: userId })
  );

  if (error) {
    throw new Error(error.message || 'Error removing member');
  }
}

export async function updateOrgMemberRole(orgId: string, userId: string, role: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.rpc('change_org_member_role', { p_org_id: orgId, p_user_id: userId, p_new_role: role })
  );

  if (error) {
    throw new Error(error.message || 'Error updating role');
  }
}

export async function transferOrgOwnership(orgId: string, newOwnerId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.rpc('transfer_org_ownership', { p_org_id: orgId, p_new_owner_id: newOwnerId })
  );

  if (error) {
    throw new Error(error.message || 'Error transferring ownership');
  }
}

export interface PendingInvite {
  id: string;
  org_id: string;
  org_name: string;
  org_avatar_url: string | null;
  email: string;
  role: string;
  invited_by: string;
  invited_by_name: string;
  created_at: string;
}

export async function getMyPendingInvites(): Promise<PendingInvite[]> {
  const { data, error } = await withRetry(() =>
    supabase.rpc('get_my_pending_invites')
  );

  if (error) {
    handleError(error, { level: 'warning', message: 'Error loading invites' });
    return [];
  }

  return data || [];
}

export async function respondToOrgInvite(inviteId: string, accept: boolean): Promise<{ status: string; org_id: string }> {
  const { data, error } = await withRetry(() =>
    supabase.rpc('respond_to_org_invite', { p_invite_id: inviteId, p_accept: accept })
  );

  if (error) {
    throw new Error(error.message || 'Error responding to invite');
  }

  return data;
}

// --- Edge Functions ---

export async function sendInviteEmail(params: {
  email: string;
  orgName: string;
  invitedByName: string;
  role: string;
}): Promise<void> {
  const { error } = await supabase.functions.invoke('send-invite-email', {
    body: params,
  });
  if (error) throw error;
}

// --- Helpers ---

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 60);
}
